import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { useAuth } from "../auth/AuthContext";
import { apiClient, formatApiErrorDetail } from "../lib/api";

function StarRow({ value, onSelect, size = 22 }) {
  return (
    <div className="flex items-center gap-1" data-testid="star-row">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onSelect && onSelect(n)}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
          className="rounded-md p-0.5 transition hover:scale-110"
          data-testid={`star-${n}`}
        >
          <Star
            size={size}
            className={n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}
          />
        </button>
      ))}
    </div>
  );
}

export default function CourseReviews({ courseId }) {
  const { user } = useAuth();
  const [data, setData] = useState({ reviews: [], average: 0, count: 0 });
  const [comments, setComments] = useState([]);
  const [myRating, setMyRating] = useState(0);
  const [myReviewText, setMyReviewText] = useState("");
  const [newComment, setNewComment] = useState("");
  const [error, setError] = useState("");
  const [savingReview, setSavingReview] = useState(false);
  const [savingComment, setSavingComment] = useState(false);

  const load = async () => {
    const [{ data: r }, { data: c }] = await Promise.all([
      apiClient.get(`/courses/${courseId}/reviews`),
      apiClient.get(`/courses/${courseId}/comments`),
    ]);
    setData(r);
    setComments(c);
    if (user) {
      const mine = r.reviews?.find((x) => x.user_id === user.user_id);
      if (mine) {
        setMyRating(mine.rating);
        setMyReviewText(mine.comment || "");
      }
    }
  };

  useEffect(() => {
    load();
  }, [courseId, user?.user_id]);

  const submitReview = async (e) => {
    e.preventDefault();
    setError("");
    if (!user) {
      setError("Please sign in to leave a review.");
      return;
    }
    if (myRating < 1) {
      setError("Select a rating from 1 to 5.");
      return;
    }
    setSavingReview(true);
    try {
      await apiClient.post(`/courses/${courseId}/reviews`, { rating: myRating, comment: myReviewText });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingReview(false);
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    setError("");
    if (!user) {
      setError("Please sign in to comment.");
      return;
    }
    if (!newComment.trim()) return;
    setSavingComment(true);
    try {
      await apiClient.post(`/courses/${courseId}/comments`, { text: newComment.trim() });
      setNewComment("");
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSavingComment(false);
    }
  };

  const removeComment = async (id) => {
    await apiClient.delete(`/comments/${id}`);
    setComments((cs) => cs.filter((c) => c.comment_id !== id));
  };

  const removeReview = async (id) => {
    if (!window.confirm("Delete this review? This cannot be undone.")) return;
    await apiClient.delete(`/reviews/${id}`);
    await load();
  };

  const [editingReviewId, setEditingReviewId] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editComment, setEditComment] = useState("");

  const beginEditReview = (r) => {
    setEditingReviewId(r.review_id);
    setEditRating(r.rating);
    setEditComment(r.comment || "");
  };

  const saveEditReview = async () => {
    await apiClient.put(`/admin/reviews/${editingReviewId}`, { rating: editRating, comment: editComment });
    setEditingReviewId(null);
    await load();
  };

  return (
    <div className="space-y-5" data-testid="course-reviews-section">
      <Card className="card-elevated">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-base font-semibold text-[#361a54]">
            <span>Reviews</span>
            <span className="flex items-center gap-2 text-sm font-normal text-slate-700">
              <StarRow value={Math.round(data.average)} onSelect={null} size={16} />
              <span data-testid="reviews-average">{data.average.toFixed(1)}</span>
              <span className="text-xs text-muted-foreground">({data.count})</span>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={submitReview} className="space-y-2 rounded-xl border border-[#e8e0f0] bg-white/80 p-3" data-testid="review-form">
            <p className="text-sm font-medium text-[#361a54]">Your rating</p>
            <StarRow value={myRating} onSelect={setMyRating} />
            <Textarea
              placeholder={user ? "Share your experience (optional)…" : "Sign in to write a review"}
              value={myReviewText}
              onChange={(e) => setMyReviewText(e.target.value)}
              className="text-sm"
              disabled={!user}
              data-testid="review-text-input"
            />
            {error && <p className="text-sm text-red-700" data-testid="review-error">{error}</p>}
            <Button type="submit" size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" disabled={savingReview || !user} data-testid="submit-review-btn">
              {savingReview ? "Saving…" : "Submit review"}
            </Button>
          </form>

          <div className="space-y-2">
            {data.reviews?.length === 0 ? (
              <p className="text-sm text-muted-foreground">No reviews yet. Be the first.</p>
            ) : (
              data.reviews.map((r) => (
                <div key={r.review_id} className="rounded-lg border border-[#e8e0f0] bg-white/70 p-3" data-testid={`review-${r.review_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-[#361a54]">{r.user_name || "Learner"}</p>
                    <div className="flex items-center gap-2">
                      {editingReviewId === r.review_id ? (
                        <StarRow value={editRating} onSelect={setEditRating} size={14} />
                      ) : (
                        <StarRow value={r.rating} onSelect={null} size={14} />
                      )}
                      {user?.role === "admin" && editingReviewId !== r.review_id && (
                        <>
                          <button
                            type="button"
                            onClick={() => beginEditReview(r)}
                            className="text-xs text-[#4a2d73] hover:underline"
                            data-testid={`edit-review-${r.review_id}`}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => removeReview(r.review_id)}
                            className="text-xs text-red-700 hover:underline"
                            data-testid={`delete-review-${r.review_id}`}
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {user?.role !== "admin" && user?.user_id === r.user_id && (
                        <button
                          type="button"
                          onClick={() => removeReview(r.review_id)}
                          className="text-xs text-red-700 hover:underline"
                          data-testid={`delete-review-${r.review_id}`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  {editingReviewId === r.review_id ? (
                    <div className="mt-2 space-y-2">
                      <Textarea
                        value={editComment}
                        onChange={(e) => setEditComment(e.target.value)}
                        className="text-sm"
                        data-testid={`edit-review-text-${r.review_id}`}
                      />
                      <div className="flex justify-end gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => setEditingReviewId(null)} data-testid={`cancel-edit-review-${r.review_id}`}>Cancel</Button>
                        <Button type="button" size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" onClick={saveEditReview} data-testid={`save-edit-review-${r.review_id}`}>Save</Button>
                      </div>
                    </div>
                  ) : (
                    r.comment && <p className="mt-1 text-sm text-slate-700">{r.comment}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="card-elevated">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-[#361a54]">Discussion</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <form onSubmit={submitComment} className="flex flex-col gap-2" data-testid="comment-form">
            <Textarea
              placeholder={user ? "Add a comment…" : "Sign in to participate in the discussion"}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="text-sm"
              disabled={!user}
              data-testid="comment-input"
            />
            <div className="flex justify-end">
              <Button type="submit" size="sm" className="bg-[#361a54] text-white hover:bg-[#2a1443]" disabled={!user || savingComment} data-testid="submit-comment-btn">
                {savingComment ? "Posting…" : "Post"}
              </Button>
            </div>
          </form>
          <div className="space-y-2">
            {comments.length === 0 ? (
              <p className="text-sm text-muted-foreground">No comments yet.</p>
            ) : (
              comments.map((c) => (
                <div key={c.comment_id} className="rounded-lg border border-[#e8e0f0] bg-white/70 p-3" data-testid={`comment-${c.comment_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-[#361a54]">{c.user_name || "Learner"}</p>
                    {(user && (user.user_id === c.user_id || user.role === "admin")) && (
                      <button
                        type="button"
                        onClick={() => removeComment(c.comment_id)}
                        className="text-xs text-red-700 hover:underline"
                        data-testid={`delete-comment-${c.comment_id}`}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{c.text}</p>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
